/**
 * QweRty protocol v2 — transport packet, manifest, and transfer identity.
 *
 * Wire layout (little-endian):
 *
 *   offset size field
 *   0      1    magic 0x51
 *   1      4    packed word
 *   5      3    dataLength (24-bit)
 *   8      N    payload
 *   8+N    4    CRC32C over bytes 0..8+N
 *
 * packed word:
 *   bits 0-15   transferId        (16-bit random per transfer)
 *   bits 16-23  reserved          (0)
 *   bits 24-28  symbolIndex       30 = manifest, 31 = RaptorQ data
 *   bit  29     isText
 *   bit  30     isLast
 *   bit  31     compressed
 *
 * A manifest packet carries the RaptorQ OTI, total length, payload hash and
 * the sender's QR settings. It is the only packet a late-joining receiver
 * needs to configure itself, and its transferId lets the receiver detect a
 * brand-new transfer and reset cleanly.
 */

import { crc32c } from '../protocol/crc32c';

export const MAGIC = 0x51;
export const HEADER_SIZE = 8;
export const CRC_SIZE = 4;
export const SYMBOL_MANIFEST = 30;
export const SYMBOL_RAPTORQ = 31;
export const PROTOCOL_VERSION = 2;
export const OTI_BYTES = 12;

export interface TransportHeader {
  transferId: number;
  symbolIndex: number;
  isText: boolean;
  isLast: boolean;
  compressed: boolean;
  dataLength: number;
}

export interface ParsedTransportPacket {
  header: TransportHeader;
  payload: Uint8Array;
}

function writeU24LE(data: Uint8Array, offset: number, value: number): void {
  data[offset] = value & 0xff;
  data[offset + 1] = (value >>> 8) & 0xff;
  data[offset + 2] = (value >>> 16) & 0xff;
}

function readU24LE(data: Uint8Array, offset: number): number {
  return (data[offset]! | (data[offset + 1]! << 8) | (data[offset + 2]! << 16)) >>> 0;
}

function writeU32LE(data: Uint8Array, offset: number, value: number): void {
  data[offset] = value & 0xff;
  data[offset + 1] = (value >>> 8) & 0xff;
  data[offset + 2] = (value >>> 16) & 0xff;
  data[offset + 3] = (value >>> 24) & 0xff;
}

function readU32LE(data: Uint8Array, offset: number): number {
  return (
    data[offset]! |
    (data[offset + 1]! << 8) |
    (data[offset + 2]! << 16) |
    ((data[offset + 3]! << 24) >>> 0)
  ) >>> 0;
}

export function serializeTransportPacket(header: TransportHeader, payload: Uint8Array): Uint8Array {
  const packet = new Uint8Array(HEADER_SIZE + payload.length + CRC_SIZE);
  packet[0] = MAGIC;

  let word = header.transferId & 0xffff;
  word |= (header.symbolIndex & 0x1f) << 24;
  word |= (header.isText ? 1 : 0) << 29;
  word |= (header.isLast ? 1 : 0) << 30;
  word |= (header.compressed ? 1 : 0) << 31;
  writeU32LE(packet, 1, word >>> 0);
  writeU24LE(packet, 5, header.dataLength);
  packet.set(payload, HEADER_SIZE);

  const crc = crc32c(packet.subarray(0, HEADER_SIZE + payload.length));
  writeU32LE(packet, HEADER_SIZE + payload.length, crc);
  return packet;
}

export function parseTransportPacket(bytes: Uint8Array): ParsedTransportPacket {
  if (bytes.length < HEADER_SIZE + CRC_SIZE) {
    throw new Error('packet too short: ' + bytes.length + ' bytes');
  }
  if (bytes[0] !== MAGIC) {
    throw new Error('bad magic byte: 0x' + bytes[0]!.toString(16));
  }
  const stored = readU32LE(bytes, bytes.length - CRC_SIZE);
  const computed = crc32c(bytes.subarray(0, bytes.length - CRC_SIZE));
  if (stored !== computed) {
    throw new Error('CRC32C mismatch');
  }

  const word = readU32LE(bytes, 1);
  const payloadLength = bytes.length - HEADER_SIZE - CRC_SIZE;
  return {
    header: {
      transferId: word & 0xffff,
      symbolIndex: (word >>> 24) & 0x1f,
      isText: ((word >>> 29) & 1) !== 0,
      isLast: ((word >>> 30) & 1) !== 0,
      compressed: ((word >>> 31) & 1) !== 0,
      dataLength: readU24LE(bytes, 5),
    },
    payload: bytes.subarray(HEADER_SIZE, HEADER_SIZE + payloadLength),
  };
}

// ─── Manifest ────────────────────────────────────────────────────────────────

export interface Manifest {
  transferId: number;
  isText: boolean;
  compressed: boolean;
  totalLength: number;
  payloadHash: number;
  qrVersion: number;
  eccLevel: number;
  parallelCount: number;
  oti: Uint8Array;
  filename: string;
  mime: string;
}

const MANIFEST_FIXED = 1 + 1 + 2 + 4 + 4 + 1 + 1 + 1 + 1 + OTI_BYTES + 2 + 2;

export function encodeManifest(manifest: Manifest): Uint8Array {
  const filename = new TextEncoder().encode(manifest.filename);
  const mime = new TextEncoder().encode(manifest.mime);
  const out = new Uint8Array(MANIFEST_FIXED + filename.length + mime.length);

  let off = 0;
  out[off++] = PROTOCOL_VERSION;
  out[off++] = (manifest.isText ? 1 : 0) | (manifest.compressed ? 2 : 0);
  out[off++] = manifest.transferId & 0xff;
  out[off++] = (manifest.transferId >>> 8) & 0xff;
  writeU32LE(out, off, manifest.totalLength); off += 4;
  writeU32LE(out, off, manifest.payloadHash >>> 0); off += 4;
  out[off++] = manifest.qrVersion;
  out[off++] = manifest.eccLevel;
  out[off++] = manifest.parallelCount;
  out[off++] = 0;
  out.set(manifest.oti.subarray(0, OTI_BYTES), off); off += OTI_BYTES;
  out[off++] = filename.length & 0xff;
  out[off++] = (filename.length >>> 8) & 0xff;
  out[off++] = mime.length & 0xff;
  out[off++] = (mime.length >>> 8) & 0xff;
  out.set(filename, off); off += filename.length;
  out.set(mime, off);
  return out;
}

export function decodeManifest(bytes: Uint8Array): Manifest {
  if (bytes.length < MANIFEST_FIXED) {
    throw new Error('manifest too short: ' + bytes.length + ' bytes');
  }
  let off = 0;
  const version = bytes[off++]!;
  if (version !== PROTOCOL_VERSION) {
    throw new Error('unsupported protocol version ' + version);
  }
  const flags = bytes[off++]!;
  const transferId = bytes[off]! | (bytes[off + 1]! << 8); off += 2;
  const totalLength = readU32LE(bytes, off); off += 4;
  const payloadHash = readU32LE(bytes, off); off += 4;
  const qrVersion = bytes[off++]!;
  const eccLevel = bytes[off++]!;
  const parallelCount = bytes[off++]!;
  off += 1;
  const oti = bytes.slice(off, off + OTI_BYTES); off += OTI_BYTES;
  const filenameLen = bytes[off]! | (bytes[off + 1]! << 8); off += 2;
  const mimeLen = bytes[off]! | (bytes[off + 1]! << 8); off += 2;
  const filename = new TextDecoder().decode(bytes.subarray(off, off + filenameLen)); off += filenameLen;
  const mime = new TextDecoder().decode(bytes.subarray(off, off + mimeLen));

  return {
    transferId,
    isText: (flags & 1) !== 0,
    compressed: (flags & 2) !== 0,
    totalLength,
    payloadHash,
    qrVersion,
    eccLevel,
    parallelCount,
    oti,
    filename,
    mime,
  };
}

export function randomTransferId(): number {
  const value = Math.floor(Math.random() * 0x10000);
  return value === 0 ? 1 : value;
}
